<template>
  <form name="form" v-on:submit.prevent="submit" v-on:change="changeHandler">
    <fieldset
      :disabled="readonly"
      style="border:0;padding:0;margin:0;min-width:0;"
    >
      <div class="card-body">
        <div class="float-right" v-if="!readonly">
          <form-button :indicator="form.processing()" v-on:clicked="submit">
            Save
          </form-button>
        </div>
        <div class="clearfix"></div>
        <hr />
        <div class="card-body-overlay" v-if="!hasValidClient">
          Please complete the "Client Details" section before the clients
          vehicle.
        </div>
        <div class="row">
          <div class="col-md-6">
            <vehicle-lookup
              :registration="form.registration"
              :show-error="form.errors.has('registration')"
              :stacked="false"
              v-on:vrm="setRegistration"
              v-on:vehicle="setVehicle"
              label="VRN"
              helper="Please enter the clients vehicle registration and click the 'Find Vehicle' button"
            ></vehicle-lookup>

            <div class="col-12">
              <div
                class="alert alert-danger"
                v-if="duplicateVehicleIncidents.length"
              >
                <span v-if="duplicateVehicleIncidents.length === 1"
                  >A claim already exists for</span
                >
                <span v-else>Multiple claims already exist with</span>
                a vehicle with this registration, this claim could already be
                set up
                <br />

                <table class="table table-borderless">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Full Name</th>
                      <th>Claim Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(duplicateVehicleIncident,
                      i) in duplicateVehicleIncidents"
                      :key="i"
                    >
                      <td>
                        <a :href="duplicateVehicleIncident.url" target="_blank">
                          {{ duplicateVehicleIncident.reference }}
                        </a>
                      </td>
                      <td>{{ duplicateVehicleIncident.fullName }}</td>
                      <td>
                        {{
                          duplicateVehicleIncident.createdAt
                            | date('DD/MM/YYYY HH:mm')
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div class="text-center">
                  <button
                    type="button"
                    class="btn btn-danger btn-xs"
                    @click="clearDuplicateVehicleIncidents"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
            <div class="clearfix"></div>

            <selectlist
              v-model="form.make_id"
              :options="makes"
              :show-error="form.errors.has('make')"
              label="Vehicle Make"
              placeholder="Please Select"
              helper="Please select the make of the vehicle"
              name="make"
            ></selectlist>

            <inputbox
              v-model="form.model"
              :show-error="form.errors.has('model')"
              name="model"
              label="Vehicle Model"
              helper="Please enter the model of the vehicle"
            ></inputbox>

            <selectlist
              v-model="form.transmission_id"
              :options="transmissions"
              name="transmission"
              placeholder="Please Select"
              label="Vehicle Transmission"
              helper="Please select the transmission type for the vehicle"
            ></selectlist>

            <inputbox
              v-model="form.colour"
              name="vehicle_colour"
              label="Vehicle Colour"
              helper="Please enter the colour of the vehicle"
            ></inputbox>

            <inputbox
              v-model="form.engine_capacity"
              name="engine_capacity"
              label="Engine Capacity (cc)"
              helper="Please enter the engine capacity of the vehicle"
            ></inputbox>

            <input-block
              v-model="form.modifications"
              name="modifications"
              label="Vehicle Modifications"
              helper="Has the clients vehicles been modified in any way? If so, please enter those details here"
            ></input-block>

            <input-block
              v-model="form.accessories"
              name="accessories"
              label="Vehicle Accessories"
              helper="Did the clients vehicles have any accessories? If so, please enter those details here"
            ></input-block>

            <fieldset>
              <legend>
                Taxi Details
                <small class="text-muted"
                  >- If this vehicle is a taxi, we need these details too</small
                >
              </legend>
            </fieldset>

            <toggleswitch
              v-model="form.taxi"
              :labels="true"
              name="taxi"
              label="Is this vehicle a taxi?"
            ></toggleswitch>

            <div v-if="form.taxi">
              <selectlist
                v-model="form.licence_authority_id"
                :options="licenceAuthorities"
                :show-error="form.errors.has('licence_authority')"
                label="Licence Authority"
                placeholder="Please Select"
                name="licence_authority"
                label-name="name"
              ></selectlist>

              <inputbox
                v-model="form.plate_number"
                :show-error="form.errors.has('plate_number')"
                name="plate_number"
                label="Plate Number"
              ></inputbox>

              <inputbox
                v-model="form.badge_number"
                :show-error="form.errors.has('badge_number')"
                name="badge_number"
                label="Badge Number"
              ></inputbox>

              <inputbox
                v-model="form.client_operator"
                :show-error="form.errors.has('client_operator')"
                name="client_operator"
                label="Client Operator"
              ></inputbox>
            </div>
            <fieldset>
              <legend>
                Location of the vehicle
                <small class="text-muted">
                  - Enter the current location of the vehicle
                </small>
              </legend>
              <address-lookup
                :address="address"
                v-on:address="setAddress"
              ></address-lookup>
            </fieldset>
          </div>

          <div class="col-md-6">
            <selectlist
              v-model="form.type_id"
              :options="types"
              :show-error="form.errors.has('type')"
              label="Vehicle Type"
              name="vehicle_type"
              placeholder="Please Select"
              helper="Please select the clients vehicle type"
            ></selectlist>

            <selectlist
              v-model="form.group_id"
              :options="groups"
              :show-error="form.errors.has('group')"
              label="Vehicle Group"
              label-name="name"
              placeholder="Please Select"
              name="group"
              helper="Please select the group for the vehicle"
            ></selectlist>

            <selectlist
              v-model="form.last_in_charge_type_id"
              :options="last_in_charge_types"
              :show-error="form.errors.has('last_in_charge_type')"
              label="Last In charge Of Vehicle"
              name="last_in_charge_type_id"
              placeholder="Please Select"
              helper="Please select who was last in charge of the vehicle"
            ></selectlist>

            <span v-if="form.last_in_charge_type_id == named_driver_type">
              <inputbox
                v-model="form.named_driver_name"
                :show-error="form.errors.has('named_driver_name')"
                name="mileage"
                label="Named Driver's Name"
                helper="Please enter the named driver's name"
              ></inputbox>

              <inputbox
                v-model="form.named_driver_contact_number"
                :show-error="form.errors.has('named_driver_contact_number')"
                name="mileage"
                label="Named Driver's Contact Number"
                helper="Please enter a contact number for the named driver"
              ></inputbox>
            </span>

            <span v-if="form.last_in_charge_type_id == other_driver_type">
              <inputbox
                v-model="form.in_charge_of_vehicle"
                :show-error="form.errors.has('in_charge_of_vehicle')"
                name="in_charge_of_vehicle"
                label="In Charge Of Vehicle"
                helper="Who Was In Charge Of The Vehicle Last?"
              ></inputbox>
            </span>

            <inputbox
              v-model="form.mileage"
              :show-error="form.errors.has('mileage')"
              name="mileage"
              label="Vehicle Mileage"
              helper="Please enter the vehicles current mileage"
            ></inputbox>

            <div
              class="form-group"
              :class="{ 'has-error': form.errors.has('registered_at') }"
            >
              <div class="col-md-4">
                <label class="control-label">Registration Date:</label>
                <p class="help-block">
                  Please enter the date the vehicle was registered
                </p>
              </div>

              <div class="col-lg-8">
                <div class="input-group">
                  <datepicker
                    name="registration_date"
                    placeholder="dd/mm/yyyy"
                    input-class="form-control"
                    format="dd/MM/yyyy"
                    :disabled="disabled"
                    v-bind:value="form.registered_at"
                    v-on:selected="setRegistrationDate"
                    v-on:input="changeHandler"
                  ></datepicker>
                  <span
                    class="text-danger"
                    v-text="form.errors.get('registered_at')"
                    v-if="form.errors.has('registered_at')"
                  ></span>
                  <div class="input-group-append">
                    <span class="input-group-text">
                      {{ age }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-8 offset-md-4">
                <div class="alert alert-info">
                  <p class="mb-0">
                    To get the tax and mot expiry dates, please go to the
                    following page:
                    <a
                      href="https://www.vehicleenquiry.service.gov.uk/"
                      target="_blank"
                      >https://www.vehicleenquiry.service.gov.uk/</a
                    >
                  </p>
                </div>
              </div>
            </div>

            <div class="form-group">
              <div class="col-md-4">
                <label class="control-label">Tax Expiry:</label>
                <p class="help-block">
                  Please enter the date the tax is due to expire on the vehicle
                </p>
              </div>
              <div class="col-lg-8">
                <datepicker
                  format="dd/MM/yyyy"
                  v-bind:value="form.tax_expires_at"
                  name="tax_expiry"
                  input-class="form-control"
                  v-on:selected="setTaxExpiry"
                  v-on:input="changeHandler"
                  placeholder="dd/mm/yyyy"
                ></datepicker>
                <span
                  class="text-danger"
                  v-text="form.errors.get('tax_expiry')"
                  v-if="form.errors.has('tax_expiry')"
                ></span>

                <div
                  class="alert alert-danger half-gutter-top"
                  v-if="isExpired(form.tax_expires_at)"
                >
                  The tax for this vehicle has expired!
                </div>
              </div>
            </div>
            <div class="form-group">
              <div class="col-md-4">
                <label class="control-label">MOT Expiry:</label>
                <p class="help-block">
                  Please enter the date the MOT expires on this vehicle
                </p>
              </div>
              <div class="col-lg-8">
                <datepicker
                  format="dd/MM/yyyy"
                  v-bind:value="form.mot_expires_at"
                  name="mot_expiry"
                  input-class="form-control"
                  v-on:selected="setMOTExpiry"
                  v-on:input="changeHandler"
                  placeholder="dd/mm/yyyy"
                ></datepicker>
                <span
                  class="text-danger"
                  v-text="form.errors.get('mot_expiry')"
                  v-if="form.errors.has('mot_expiry')"
                ></span>

                <div
                  class="alert alert-danger half-gutter-top"
                  v-if="isExpired(form.mot_expires_at)"
                >
                  The MOT for this vehicle has expired!
                </div>
              </div>
            </div>

            <toggleswitch
              :labels="true"
              name="driveable"
              label="Is the vehicle roadworthy?"
              v-model="form.driveable"
              helper="Is the clients vehicle still driveable?"
            ></toggleswitch>

            <toggleswitch
              :labels="true"
              name="secure"
              label="Is the vehicle secure?"
              v-model="form.secure"
              helper="Is the clients vehicle secure?"
            ></toggleswitch>

            <toggleswitch
              :labels="true"
              name="secure"
              label="Has the vehicle been recovered?"
              v-model="form.vehicle_recovered"
              v-if="source?.incident_claim_type?.name === 'theft'"
            ></toggleswitch>

            <div
              class="form-group has-helper"
              v-if="
                form?.vehicle_recovered &&
                  source?.incident_claim_type?.name === 'theft'
              "
              :class="{ 'has-error': form.errors.has('vehicle_recovered_at') }"
            >
              <div class="col-md-4">
                <label class="control-label"
                  >Vehicle Recovered Date/Time:</label
                >
                <p class="help-block">
                  When was the vehicle recovered?
                </p>
              </div>
              <div class="col-md-8">
                <datetimepicker
                  :value="form.vehicle_recovered_at"
                  name="vehicle_recovered_at"
                  v-on:selected="setRecoveredDate"
                  input-class="form-control"
                ></datetimepicker>
                <span
                  class="text-danger"
                  v-text="form.errors.get('vehicle_recovered_at')"
                  v-if="form.errors.has('vehicle_recovered_at')"
                ></span>
              </div>
            </div>

            <toggleswitch
              :labels="true"
              name="owner_registered_keeper"
              label="owner and registered keeper"
              v-model="form.owner_registered_keeper"
              helper="Were you the owner and registered keeper of the vehicle?"
            ></toggleswitch>

            <toggleswitch
              :labels="true"
              name="have_outstanding_finance"
              label="Outstanding Finance"
              v-model="form.have_outstanding_finance"
              helper="Any Outstanding Finance?"
            ></toggleswitch>

            <inputbox
              v-model="form.total_outstanding_finance"
              :show-error="form.errors.has('total_outstanding_finance')"
              v-if="form.have_outstanding_finance"
              name="total_outstanding_finance"
              label="Outstanding Finance"
              helper="How much finance is outstanding?"
            ></inputbox>
          </div>
        </div>

        <div v-if="source?.incident_claim_type?.name === 'theft'">
          <fieldset>
            <legend>
              Security On Vehicle
              <small class="text-muted">
                - Provide the details of security on vehicle
              </small>
            </legend>
          </fieldset>

          <inputbox
            v-model="form.type_of_security"
            :show-error="form.errors.has('type_of_security')"
            name="type_of_security"
            label="Type of security"
          ></inputbox>

          <inputbox
            v-model="form.mileage_at_point_of_theft"
            :show-error="form.errors.has('mileage_at_point_of_theft')"
            name="mileage_at_point_of_theft"
            label="Mileage At Point Of Theft"
          ></inputbox>

          <div class="row">
            <div class="col-md-6">
              <toggleswitch
                :labels="true"
                name="d_lock"
                label="D-Lock"
                v-model="form.d_lock"
              ></toggleswitch>

              <toggleswitch
                :labels="true"
                name="disk_lock"
                label="Disk Lock"
                v-model="form.disk_lock"
              ></toggleswitch>

              <toggleswitch
                :labels="true"
                name="grip_lock"
                label="Grip Lock"
                v-model="form.grip_lock"
              ></toggleswitch>

              <toggleswitch
                :labels="true"
                name="armoured_chain"
                label="Armoured Chain"
                v-model="form.armoured_chain"
              ></toggleswitch>

              <toggleswitch
                :labels="true"
                name="other"
                label="Other"
                v-model="form.other"
              ></toggleswitch>
            </div>

            <div class="col-md-6">
              <toggleswitch
                :labels="true"
                name="tracker"
                label="Tracker"
                v-model="form.tracker"
              ></toggleswitch>

              <toggleswitch
                :labels="true"
                name="alarm"
                label="Alarm"
                v-model="form.alarm"
              ></toggleswitch>

              <toggleswitch
                :labels="true"
                name="immobiliser"
                label="Immobiliser"
                v-model="form.immobiliser"
              ></toggleswitch>

              <toggleswitch
                :labels="true"
                name="steering_lock"
                label="Steering Lock"
                v-model="form.steering_lock"
              ></toggleswitch>
            </div>
          </div>

          <input-block
            v-model="form.other_details"
            v-if="form.other"
            name="Details"
            label="Other Details"
            helper="Add any additional information details here."
          ></input-block>
        </div>

        <fieldset>
          <legend>
            Damage Details
            <small class="text-muted">
              - Please click on the sections of the car to indicate damage.
            </small>
          </legend>
        </fieldset>

        <photo-upload :readonly="readonly" :incident="incident"></photo-upload>

        <vehicle-damage
          :type="form.type_id"
          :damage="damage"
          :readonly="readonly"
          ref="vehicleDamage"
        ></vehicle-damage>
      </div>

      <div class="card-footer text-right" v-if="!readonly">
        <form-button :indicator="form.processing()" v-on:clicked="submit">
          Save
        </form-button>
      </div>
    </fieldset>
  </form>
</template>

<script>
import PhotoUpload from './Children/PhotoUpload.vue';
import VehicleLookup from './../../../Generic/VehicleLookup.vue';
import Form from './../../../../Classes/Form';
import VehicleDamage from './../../Shared/VehicleDamage';
import debounce from 'lodash/debounce';

export default {
  components: {
    VehicleLookup,
    'photo-upload': PhotoUpload,
    'vehicle-damage': VehicleDamage
  },

  props: {
    incident: {
      type: String,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      disabled: {
        from: new Date()
      },
      last_in_charge_types: [],
      named_driver_type: null,
      other_driver_type: null,
      duplicateVehicleIncidents: [],
      form: new Form({
        identifier: null,
        registration: null,
        type_id: null,
        group_id: null,
        mileage: null,
        make_id: null,
        model: null,
        transmission_id: null,
        registered_at: null,
        colour: null,
        engine_capacity: null,
        tax_expires_at: null,
        mot_expires_at: null,
        taxi: false,
        licence_authority_id: null,
        plate_number: null,
        badge_number: null,
        client_operator: null,
        modifications: null,
        accessories: null,
        last_in_charge_type_id: null,
        named_driver_name: null,
        named_driver_contact_number: null,
        in_charge_of_vehicle: null,
        driveable: false,
        secure: false,
        owner_registered_keeper: false,
        have_outstanding_finance: false,
        total_outstanding_finance: null,
        have_cbt: false,
        own_the_vehicle: false,
        v5_in_your_name: false,
        provide_license_share_code: false,
        license_share_code: null,
        address_line_one: null,
        address_line_two: null,
        city: null,
        postcode: null,
        type_of_security: null,
        mileage_at_point_of_theft: null,
        d_lock: false,
        tracker: false,
        disk_lock: false,
        alarm: false,
        grip_lock: false,
        immobiliser: false,
        armoured_chain: false,
        steering_lock: false,
        other: false,
        other_details: null,
        vehicle_recovered: false,
        vehicle_recovered_at: null
      }),
      data_entered: false
    };
  },

  computed: {
    source() {
      return this.$store.getters['Incident/source'];
    },

    client() {
      return this.$store.getters['Incident/client'];
    },

    types() {
      return this.$store.getters['VehicleTypes/all'];
    },

    groups() {
      return this.$store.getters['VehicleGroups/all'];
    },

    makes() {
      return this.$store.getters['VehicleMakes/all'];
    },

    transmissions() {
      return this.$store.getters['VehicleTransmissions/all'];
    },

    licenceAuthorities() {
      return this.$store.getters['LicenceAuthorities/all'];
    },

    age() {
      if (!this.form.registered_at) {
        return 'Please select a date';
      }

      let yearsOld = window.DateManager.differenceInYears(
        new Date(),
        this.form.registered_at
      ).toString();

      return yearsOld + ' Years ';
    },

    vehicle() {
      return this.$store.getters['Incident/clientVehicle'];
    },

    damage() {
      return this.vehicle?.damage;
    },

    hasValidClient() {
      if (!this.client) {
        return false;
      }

      if (!Object.keys(this.client).length) {
        return false;
      }

      return true;
    },

    address() {
      return {
        address_line_one: this.form.address_line_one,
        address_line_two: this.form.address_line_two,
        city: this.form.city,
        postcode: this.form.postcode
      };
    }
  },

  mounted() {
    this.mount();
  },

  methods: {
    mount() {
      if (this.hasValidClient) {
        this.getClientVehicle();
      }

      window.Ajax.get('/last-in-charge-types')
        .then(response => {
          this.last_in_charge_types = response.data;
          const namedDriver = this.last_in_charge_types.find(
            type => type.name == 'named_driver'
          );

          if (namedDriver) {
            this.named_driver_type = namedDriver.id;
          }

          const OtherDriver = this.last_in_charge_types.find(
            type => type.name == 'other'
          );

          if (OtherDriver) {
            this.other_driver_type = OtherDriver.id;
          }
        })
        .catch(response => {});
    },

    getClientVehicle() {
      let vehicle = this.$store.getters['Incident/clientVehicle'];

      if (vehicle && Object.keys(vehicle).length) {
        this.form.registration = vehicle.registration;
        this.form.group_id = vehicle.group_id;
        this.form.make_id = vehicle.make_id;
        this.form.model = vehicle.model;
        this.form.mileage = vehicle.mileage;
        this.form.registered_at = vehicle.registered_at
          ? vehicle.registered_at
          : null;
        this.form.colour = vehicle.colour;
        this.form.engine_capacity = vehicle.engine_capacity;
        this.form.tax_expires_at = vehicle.tax_expires_at
          ? vehicle.tax_expires_at
          : null;
        this.form.mot_expires_at = vehicle.mot_expires_at
          ? vehicle.mot_expires_at
          : null;
        this.form.taxi = vehicle.taxi;
        this.form.licence_authority_id = vehicle.licence_authority_id;
        this.form.plate_number = vehicle.plate_number;
        this.form.badge_number = vehicle.badge_number;
        this.form.client_operator = vehicle.client_operator;
        this.form.type_id = vehicle.type_id;
        this.form.transmission_id = vehicle.transmission_id;
        this.form.modifications = vehicle.modifications;
        this.form.accessories = vehicle.accessories;
        this.form.last_in_charge_type_id = vehicle.last_in_charge_type_id;
        this.form.named_driver_name = vehicle.named_driver_name;
        this.form.named_driver_contact_number =
          vehicle.named_driver_contact_number;
        this.form.in_charge_of_vehicle = vehicle.in_charge_of_vehicle;
        this.form.driveable = vehicle.driveable;
        this.form.secure = vehicle.secure;
        this.form.owner_registered_keeper = vehicle.owner_registered_keeper;
        this.form.have_outstanding_finance = vehicle.have_outstanding_finance;
        this.form.total_outstanding_finance = vehicle.total_outstanding_finance;
        this.form.have_cbt = vehicle.have_cbt;
        this.form.own_the_vehicle = vehicle.own_the_vehicle;
        this.form.v5_in_your_name = vehicle.v5_in_your_name;
        this.form.provide_license_share_code =
          vehicle.provide_license_share_code;
        this.form.license_share_code = vehicle.license_share_code;
        this.form.address_line_one = vehicle.address_line_one;
        this.form.address_line_two = vehicle.address_line_two;
        this.form.city = vehicle.city;
        this.form.postcode = vehicle.postcode;
        this.form.vehicle_recovered = vehicle.vehicle_recovered || false;
        this.form.vehicle_recovered_at = vehicle.vehicle_recovered_at;
        this.form.type_of_security = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.type_of_security
          : null;
        this.form.mileage_at_point_of_theft = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.mileage_at_point_of_theft
          : null;
        this.form.d_lock = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.d_lock
          : null;
        this.form.tracker = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.tracker
          : null;
        this.form.disk_lock = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.disk_lock
          : null;
        this.form.alarm = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.alarm
          : null;
        this.form.grip_lock = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.grip_lock
          : null;
        this.form.immobiliser = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.immobiliser
          : null;
        this.form.armoured_chain = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.armoured_chain
          : null;
        this.form.steering_lock = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.steering_lock
          : null;
        this.form.other = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.other
          : null;
        this.form.other_details = vehicle.security_on_vehicle
          ? vehicle.security_on_vehicle.other_details
          : null;
      }
    },

    /**
     * If the user tries to change tab when there are unsaved changes in the form we
     * want to display a popup warning them they have unsaved changes.
     *
     * To do that we add a form @change event listener to the form and then emit an
     * 'unsaved-data' event whenever a form value changes. This is then picked up
     * in the parent component and that handles displaying the warning if the user
     * tries to change tabs.
     */
    changeHandler() {
      if (!this.data_entered) {
        this.data_entered = true;
      }
      this.$emit('unsaved-data', this.data_entered);
    },

    submit() {
      if (this.readonly) return;
      window.toastify.info('Saving', "We're saving those details for you.");

      this.form
        .post(
          `/incidents/${this.incident}/client/${this.client.identifier}/vehicle`
        )
        .then(response => {
          this.$emit('updated', response.completed);
          this.$store.commit('Incident/setClientVehicle', response);

          if (this.$refs.vehicleDamage.damageForm) {
            this.$refs.vehicleDamage.damageForm
              .post(
                `/incidents/${this.incident}/client/${this.client.identifier}/vehicle/${this.vehicle.identifier}/damage`
              )
              .then(response => {
                this.submitSuccess();

                this.$store.commit('Incident/setDamageDetails', response);
                this.$store.commit('Incident/setDamageDetails', response);
              })
              .catch(error => {
                this.indicator = false;
                window.toastify.error(
                  'Error',
                  "Sorry, there was an error when saving the client's vehicle."
                );
              });
          } else {
            this.submitSuccess();
          }
        })
        .catch(response => {
          window.toastify.error(
            'Error',
            "Sorry, there was an error saving the client's vehicle."
          );
        });
    },

    submitSuccess() {
      window.toastify.success('Saved', "We've updated those details for you.");
      this.indicator = false;
      // reset monitoring of form changes
      this.data_entered = false;
      this.$emit('unsaved-data', this.data_entered);
    },

    setRegistration: debounce(function(registration) {
      if (registration != this.form.registration) {
        this.form.registration = registration;

        if (registration) {
          window.Ajax.get(`/incidents?filter[terms]=${registration}`)
            .then(response => {
              if (response.data?.length > 0) {
                this.duplicateVehicleIncidents = response.data
                  .filter(incident => incident.id != this.client.incident_id)
                  .map(incident => ({
                    reference: incident.source?.claim_reference,
                    url: this.incidentUrl(incident),
                    fullName: incident.client?.full_name,
                    createdAt: incident.created_at
                  }));
              } else {
                this.duplicateVehicleIncidents = [];
              }
            })
            .catch(err => {});
        }
      }
    }, 400),

    clearDuplicateVehicleIncidents() {
      this.duplicateVehicleIncidents = [];
    },

    setVehicle(vehicle) {
      this.form.transmission_id = vehicle.transmission_type_id;
      this.form.group_id = vehicle.group_id;
      this.form.make_id = vehicle.make_id;
      this.form.model = vehicle.model;
      this.form.registered_at = vehicle.registered_at;
      this.form.colour = vehicle.colour;
      this.form.engine_capacity = vehicle.engine_capacity;

      if (vehicle.tax_expires_at) {
        this.form.tax_expires_at = vehicle.tax_expires_at;
      }

      if (vehicle.mot_expires_at) {
        this.form.mot_expires_at = vehicle.mot_expires_at;
      }
    },

    setAddress(address) {
      this.form.address_line_one = address.address_line_one;
      this.form.address_line_two = address.address_line_two;
      this.form.city = address.city;
      this.form.postcode = address.postcode;
    },

    setRegistrationDate(value) {
      this.form.registered_at = value;
    },

    setTaxExpiry(value) {
      this.form.tax_expires_at = value;
    },

    setMOTExpiry(value) {
      this.form.mot_expires_at = value;
    },

    setRecoveredDate(value) {
      this.form.vehicle_recovered_at = value;
    },

    isExpired(expiryDate) {
      if (expiryDate) {
        return Date.now() > new Date(expiryDate).getTime();
      }

      return false;
    }
  },

  watch: {
    $route(to, from) {
      this.mount();
    }
  }
};
</script>
